import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { lazy, Suspense, useEffect, useRef } from "react";
import { ErrorBoundary } from "@/components/common";
import { logInfo } from "@/lib/logger";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Toaster } from "@strivelabs/ui-core";
import { useOrganization, useOrganizationList } from "@clerk/clerk-react";
import { ChartsDemo } from "./components/demo/ChartsDemo";
import { useTenantDetails } from "@/hooks/useConnections";
import { useAppStore } from "@/store/appStore";
import { ContainerDemo } from "./components/demo/ContainerDemo";
import { InsightsDemo } from "./components/demo/InsightsDemo";
import { ChatProvider } from "@/contexts/ChatContext";

// Lazy loading pages for better performance
const SignIn = lazy(() => import("@/pages/SignIn"));
const HomeOrWorkforcePage = lazy(() => import("@/pages/HomeOrWorkforcePage"));
const MyWorkforce = lazy(() => import("@/pages/MyWorkforce"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Settings = lazy(() => import("@/pages/Settings"));
const AgentLayout = lazy(() => import("@/pages/AgentLayout"));
const InsightReport = lazy(() => import("@/pages/InsightReport"));
const AnswerEngineOptimization = lazy(() => import("@/agents/answer-engine-optimization/AnswerEngineOptimization"))
const BlockKitPlayground = lazy(() => import("@/pages/BlockKitPlayground"));
const BlogPostStrategizerLayout = lazy(() => import("@/agents/blog-post-content-strategizer/BlogPostStrategizerLayout"));
const BlogPostRefresherLayout = lazy(() => import("@/agents/blog-post-content-refresher/BlogPostRefresherLayout"));
const PdfRenderer = lazy(() => import("@/components/pdf/PdfRenderer"));

// Onboarding flow components
const OnboardingLayout = lazy(() => import("@/pages/onboarding/OnboardingLayout").then(m => ({ default: m.OnboardingLayout })));
const WelcomeStep = lazy(() => import("@/pages/onboarding/WelcomeStep").then(m => ({ default: m.WelcomeStep })));
const CompanyInfoStep = lazy(() => import("@/pages/onboarding/CompanyInfoStep").then(m => ({ default: m.CompanyInfoStep })));
const BrandCompetitorsStep = lazy(() => import("@/pages/onboarding/BrandCompetitorsStep").then(m => ({ default: m.BrandCompetitorsStep })));

// @demo: uncomment routes in demoRoutes.tsx to include them in the build
import { DemoRoutes } from "@/demoRoutes";

// Loading component for suspense fallback
const Loading = () => {
  logInfo('Loading component rendered', { component: 'App Loading' });
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin h-8 w-8 border-4 border-orange-500 rounded-full border-t-transparent"></div>
    </div>
  );
};

// Layout component for protected routes
const ProtectedLayout = () => (
  <ErrorBoundary>
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  </ErrorBoundary>
);

export default function App() {
  logInfo('App component rendered');

  // setting identity to identify the user in analytics
  useAnalytics();

  // Auto-select and set the first available organization as active
  const { isLoaded: isActiveOrgDetailsLoaded, organization } = useOrganization();

  // Set tenant details in global store — only fetch when an org is active
  // (first-time users going through onboarding don't have an org/tenant yet)
  const { data: tenantDetails } = useTenantDetails({ enabled: Boolean(organization) });
  useEffect(() => {
    if (tenantDetails) {
      useAppStore.getState().setTenantDetails(tenantDetails);
      logInfo('App: Tenant details set in global store', { tenantId: tenantDetails.tenant.id });
    }
  }, [tenantDetails]);
  const { isLoaded: isOrgListLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const orgInitRef = useRef(false);

  useEffect(() => {
    // Avoid repeated execution
    if (orgInitRef.current) return;

    // If active org is already available, do nothing
    if (isActiveOrgDetailsLoaded && organization) {
      orgInitRef.current = true;
      return;
    }

    // Wait until both org context and membership list are loaded
    if (!isActiveOrgDetailsLoaded || !isOrgListLoaded) return;

    // Pick first org from memberships and set it active
    const firstMembership = userMemberships?.data?.[0];
    if (firstMembership?.organization) {
      setActive({ organization: firstMembership.organization.id })
        .then(() => {
          orgInitRef.current = true;
          logInfo('App: Set first organization as active', {
            orgId: firstMembership.organization.id,
          });
        })
        .catch(() => {
          // Non-blocking: just mark as initialized to prevent loops
          orgInitRef.current = true;
        });
    } else {
      // No orgs available; mark as initialized
      orgInitRef.current = true;
    }
  }, [isActiveOrgDetailsLoaded, isOrgListLoaded, organization, userMemberships, setActive]);

  return (
    <ChatProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Protected Routes */}
              <Route element={<ProtectedLayout />}>
                <Route path="/ui" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/chat/:executionId" element={<HomeOrWorkforcePage />} />
                <Route path="/ui/my-workforce" element={<MyWorkforce />} />
                <Route path="/ui/marketplace" element={<Marketplace />} />
                <Route path="/ui/settings" element={<Settings />} />
                <Route path="/ui/reports/:insightId" element={<InsightReport />} />
                <Route path="/ui/agent/answer-engine-optimization" element={<AnswerEngineOptimization />} />
                <Route path="/ui/agent/blog-post-content-strategizer/*" element={<BlogPostStrategizerLayout />} />
                <Route path="/ui/agent/blog-post-content-refresher/*" element={<BlogPostRefresherLayout />} />

                {/* @demo: uncomment routes in demoRoutes.tsx to activate them */}
                {DemoRoutes}

                <Route path="/ui/agent/:agentId" element={<AgentLayout />} />
              </Route>

              {/* Public/Special Routes with ErrorBoundary only */}
              <Route path="/ui/charts" element={
                <ErrorBoundary>
                  <ChartsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/slots" element={
                <ErrorBoundary>
                  <ContainerDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/insights" element={
                <ErrorBoundary>
                  <InsightsDemo />
                </ErrorBoundary>
              } />
              <Route path="/ui/block-kit-playground" element={
                <ErrorBoundary>
                  <BlockKitPlayground />
                </ErrorBoundary>
              } />
              <Route path="/ui/pdf-renderer" element={
                <ErrorBoundary>
                  <PdfRenderer />
                </ErrorBoundary>
              } />
              <Route path="/ui/onboarding" element={
                <ErrorBoundary>
                  <OnboardingLayout />
                </ErrorBoundary>
              }>
                <Route index element={<Navigate to="welcome" replace />} />
                <Route path="welcome" element={<WelcomeStep />} />
                <Route path="company-info" element={<CompanyInfoStep />} />
                <Route path="brand" element={<BrandCompetitorsStep />} />
              </Route>
              <Route path="/ui/sign-in" element={
                <ErrorBoundary>
                  <SignIn />
                </ErrorBoundary>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <Toaster />
      </BrowserRouter>
    </ChatProvider>
  );
}
